import { useState, useEffect, useCallback } from "react";

// ============================================================
// GAME ENGINE
// ============================================================

const SEQUENCE_LENGTH = 5;
const MAX_ATTEMPTS = 6;
// Top-level switch for sequence seed generation.
// Use "daily" to keep date-based deterministic games, or "random" for a fresh seed every new game.
const SEED_MODE = "random";

const RULES = {
  // EASY
  arithmetic: { label: { it: "Progressione Aritmetica", en: "Arithmetic Progression" }, difficulty: "easy" },
  arithmetic_desc: { label: { it: "Progressione Decrescente", en: "Descending Progression" }, difficulty: "easy" },
  even: { label: { it: "Numeri Pari", en: "Even Numbers" }, difficulty: "easy" },
  odd: { label: { it: "Numeri Dispari", en: "Odd Numbers" }, difficulty: "easy" },
  // MEDIUM
  geometric: { label: { it: "Progressione Geometrica", en: "Geometric Progression" }, difficulty: "medium" },
  fibonacci: { label: { it: "Fibonacci", en: "Fibonacci" }, difficulty: "medium" },
  squares: { label: { it: "Quadrati Perfetti", en: "Perfect Squares" }, difficulty: "medium" },
  cubes: { label: { it: "Cubi", en: "Cubes" }, difficulty: "medium" },
  primes: { label: { it: "Numeri Primi", en: "Prime Numbers" }, difficulty: "medium" },
  // HARD
  alternating: { label: { it: "Alternanza +×", en: "Alternating +×" }, difficulty: "hard" },
  growing_diff: { label: { it: "Differenze Crescenti", en: "Growing Differences" }, difficulty: "hard" },
  triangular: { label: { it: "Numeri Triangolari", en: "Triangular Numbers" }, difficulty: "hard" },
};

const TEXTS = {
  it: {
    subtitle: "INDOVINA LA SEQUENZA MATEMATICA",
    chooseDifficulty: "SCEGLI DIFFICOLTÀ",
    difficulty: { easy: "FACILE", medium: "MEDIO", hard: "DIFFICILE" },
    attempts: (used, max) => `${used}/${max} tentativi`,
    deleteAction: "← DEL",
    submitAction: "CONFERMA ▶",
    moveRightAction: "DESTRA →",
    hintTitle: "💡 SUGGERIMENTO",
    hints: {
      arithmetic: "La sequenza cresce di un valore costante",
      arithmetic_desc: "La sequenza decresce di un valore costante",
      even: "Tutti i numeri sono pari",
      odd: "Tutti i numeri sono dispari",
      geometric: "Ogni numero è moltiplicato per lo stesso fattore",
      fibonacci: "Ogni numero è la somma dei due precedenti",
      squares: "I numeri sono potenze al quadrato",
      cubes: "I numeri sono potenze al cubo",
      primes: "Tutti i numeri sono primi",
      alternating: "La sequenza alterna addizione e moltiplicazione",
      growing_diff: "Le differenze tra i numeri crescono progressivamente",
      triangular: "Sono i numeri triangolari: n(n+1)/2",
    },
    defaultHint: "Segui la regola matematica",
    stuckSuggestionPrefix: "🔎 SUGGERIMENTO: uno dei numeri della sequenza è",
    validationError: "Inserisci tutti i numeri prima di confermare!",
    legend: { correct: "Corretto", present: "Presente", absent: "Assente" },
    changeDifficulty: "cambia difficoltà",
    resultVictory: "VITTORIA!",
    resultGameOver: "GAME OVER",
    sequenceLabel: "Sequenza",
    ruleLabel: "Regola",
    playAgain: "GIOCA ANCORA",
    pointsLabel: "pts",
    langLabel: "LANG",
  },
  en: {
    subtitle: "GUESS THE MATH SEQUENCE",
    chooseDifficulty: "CHOOSE DIFFICULTY",
    difficulty: { easy: "EASY", medium: "MEDIUM", hard: "HARD" },
    attempts: (used, max) => `${used}/${max} attempts`,
    deleteAction: "← DELETE",
    submitAction: "SUBMIT ▶",
    moveRightAction: "RIGHT →",
    hintTitle: "💡 HINT",
    hints: {
      arithmetic: "The sequence increases by a constant value",
      arithmetic_desc: "The sequence decreases by a constant value",
      even: "All numbers are even",
      odd: "All numbers are odd",
      geometric: "Each number is multiplied by the same factor",
      fibonacci: "Each number is the sum of the previous two",
      squares: "These numbers are perfect squares",
      cubes: "These numbers are cubes",
      primes: "All numbers are prime",
      alternating: "The sequence alternates addition and multiplication",
      growing_diff: "Differences between numbers grow progressively",
      triangular: "These are triangular numbers: n(n+1)/2",
    },
    defaultHint: "Follow the math rule",
    stuckSuggestionPrefix: "🔎 SUGGESTION: one number in the sequence is",
    validationError: "Enter all numbers before submitting!",
    legend: { correct: "Correct", present: "Present", absent: "Absent" },
    changeDifficulty: "change difficulty",
    resultVictory: "VICTORY!",
    resultGameOver: "GAME OVER",
    sequenceLabel: "Sequence",
    ruleLabel: "Rule",
    playAgain: "PLAY AGAIN",
    pointsLabel: "pts",
    langLabel: "LANG",
  },
};

function seededRandom(seed) {
  let s = seed;
  return function () {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

function getDateSeed() {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

function randInt(rng, min, max) {
  return min + Math.floor(rng() * (max - min + 1));
}

function generateSequence(difficulty = "easy", rng = Math.random.bind(Math)) {
  const rules = Object.entries(RULES)
    .filter(([, v]) => v.difficulty === difficulty)
    .map(([k]) => k);

  for (let attempt = 0; attempt < 20; attempt++) {
    const rule = rules[Math.floor(rng() * rules.length)];
    const seq = applyRule(rule, rng);
    if (seq && validateSequence(seq)) return { sequence: seq, rule };
  }
  // fallback
  const start = randInt(rng, 1, 20);
  const step = randInt(rng, 1, 10);
  const seq = Array.from({ length: SEQUENCE_LENGTH }, (_, i) => start + step * i);
  return { sequence: seq, rule: "arithmetic" };
}

function applyRule(rule, rng) {
  try {
    switch (rule) {
      case "arithmetic": {
        const start = randInt(rng, 1, 30);
        const step = randInt(rng, 1, 10);
        return Array.from({ length: SEQUENCE_LENGTH }, (_, i) => start + step * i);
      }
      case "arithmetic_desc": {
        const start = randInt(rng, 40, 90);
        const step = randInt(rng, 1, 10);
        return Array.from({ length: SEQUENCE_LENGTH }, (_, i) => start - step * i);
      }
      case "even": {
        const n = randInt(rng, 1, 15);
        return Array.from({ length: SEQUENCE_LENGTH }, (_, i) => 2 * (n + i));
      }
      case "odd": {
        const n = randInt(rng, 1, 15);
        return Array.from({ length: SEQUENCE_LENGTH }, (_, i) => 2 * (n + i) + 1);
      }
      case "geometric": {
        const a1 = randInt(rng, 1, 4);
        const r = randInt(rng, 2, 3);
        return Array.from({ length: SEQUENCE_LENGTH }, (_, i) => a1 * Math.pow(r, i));
      }
      case "fibonacci": {
        const a = randInt(rng, 1, 5);
        const b = randInt(rng, 1, 8);
        const seq = [a, b];
        for (let i = 2; i < SEQUENCE_LENGTH; i++) seq.push(seq[i - 1] + seq[i - 2]);
        return seq;
      }
      case "squares": {
        const offset = randInt(rng, 0, 5);
        return Array.from({ length: SEQUENCE_LENGTH }, (_, i) => Math.pow(i + 1 + offset, 2));
      }
      case "cubes": {
        const offset = randInt(rng, 0, 2);
        return Array.from({ length: SEQUENCE_LENGTH }, (_, i) => Math.pow(i + 1 + offset, 3));
      }
      case "primes": {
        const allPrimes = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47, 53, 59, 61, 67, 71, 73, 79, 83, 89, 97];
        const start = randInt(rng, 0, allPrimes.length - SEQUENCE_LENGTH - 1);
        return allPrimes.slice(start, start + SEQUENCE_LENGTH);
      }
      case "alternating": {
        const a = randInt(rng, 1, 5);
        const b = randInt(rng, 2, 4);
        let v = randInt(rng, 2, 10);
        const seq = [v];
        for (let i = 1; i < SEQUENCE_LENGTH; i++) {
          v = i % 2 === 1 ? v * b : v + a;
          seq.push(v);
        }
        return seq;
      }
      case "growing_diff": {
        const start = randInt(rng, 1, 10);
        const base_diff = randInt(rng, 2, 4);
        const seq = [start];
        for (let i = 1; i < SEQUENCE_LENGTH; i++) seq.push(seq[i - 1] + base_diff + i - 1);
        return seq;
      }
      case "triangular": {
        const offset = randInt(rng, 0, 5);
        return Array.from({ length: SEQUENCE_LENGTH }, (_, i) => {
          const n = i + 1 + offset;
          return (n * (n + 1)) / 2;
        });
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
}

function validateSequence(seq) {
  if (!seq || seq.length !== SEQUENCE_LENGTH) return false;
  return seq.every((n) => Number.isInteger(n) && n >= 0 && n <= 99);
}

function evaluateGuess(guess, sequence) {
  return guess.map((g, i) => {
    if (g === sequence[i]) return "correct";
    if (sequence.includes(g)) return "present";
    return "absent";
  });
}

// ============================================================
// GAME SEED CONFIGURATION
// ============================================================

// Returns a stable difficulty offset so each difficulty still maps to a distinct seed family.
function getDifficultySeedOffset(difficulty) {
  return difficulty === "medium" ? 1000 : difficulty === "hard" ? 2000 : 0;
}

// Builds the game data using the configured seed mode through one centralized path.
function getConfiguredGame(difficulty) {
  const seed = SEED_MODE === "daily"
    ? getDateSeed() + getDifficultySeedOffset(difficulty)
    : Math.floor(Math.random() * 0xffffffff);
  const rng = seededRandom(seed);
  return generateSequence(difficulty, rng);
}

// ============================================================
// COMPONENTS
// ============================================================

const COLORS = {
  bg: "#0a0a1a",
  panel: "#0d0d2b",
  accent1: "#ff00ff",
  accent2: "#00ffff",
  accent3: "#ff6b35",
  correct: "#00ff9f",
  present: "#ffcc00",
  absent: "#1a1a3e",
  text: "#e0e0ff",
  grid_border: "#2a2a5a",
};

function SynthwaveBackground() {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 0, overflow: "hidden",
      background: `linear-gradient(180deg, #0a0015 0%, #0a0a1a 40%, #0d0d2b 100%)`,
    }}>
      {/* Grid floor */}
      <div style={{
        position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)",
        width: "200vw", height: "55vh",
        background: `
          linear-gradient(180deg, transparent 0%, #0a0a1a44 100%),
          repeating-linear-gradient(90deg, transparent, transparent 60px, #ff00ff22 60px, #ff00ff22 61px),
          repeating-linear-gradient(0deg, transparent, transparent 40px, #ff00ff22 40px, #ff00ff22 41px)
        `,
        transformOrigin: "bottom center",
        transform: "translateX(-50%) perspective(400px) rotateX(60deg)",
      }} />
      {/* Sun */}
      <div style={{
        position: "absolute", top: "28%", left: "50%", transform: "translateX(-50%)",
        width: 260, height: 260, borderRadius: "50%",
        background: "linear-gradient(180deg, #ff6b35 0%, #ff00ff 50%, #0a0a1a 100%)",
        boxShadow: "0 0 80px 40px #ff00ff44, 0 0 200px 80px #ff006622",
        overflow: "hidden",
      }}>
        {[0.38, 0.46, 0.54, 0.62, 0.70].map((pos, i) => (
          <div key={i} style={{
            position: "absolute", width: "100%", height: 14,
            background: "#0a0a1a",
            top: `${pos * 100}%`,
          }} />
        ))}
      </div>
      {/* Stars */}
      {Array.from({ length: 40 }).map((_, i) => (
        <div key={i} style={{
          position: "absolute",
          left: `${(i * 37.3) % 100}%`,
          top: `${(i * 19.7) % 45}%`,
          width: i % 5 === 0 ? 3 : 1,
          height: i % 5 === 0 ? 3 : 1,
          borderRadius: "50%",
          background: i % 3 === 0 ? "#00ffff" : "#ffffff",
          opacity: 0.4 + (i % 5) * 0.1,
          animation: `twinkle ${2 + (i % 3)}s ease-in-out infinite`,
          animationDelay: `${(i * 0.3) % 3}s`,
        }} />
      ))}
    </div>
  );
}

function Cell({ value, status, isActive, isCurrentRow, colIndex, revealed, onClick }) {
  const colorMap = {
    correct: { bg: COLORS.correct, border: COLORS.correct, glow: `0 0 18px ${COLORS.correct}88`, text: "#000" },
    present: { bg: COLORS.present, border: COLORS.present, glow: `0 0 18px ${COLORS.present}88`, text: "#000" },
    absent: { bg: "#1a1a3e", border: "#2a2a6a", glow: "none", text: COLORS.text },
    default: { bg: "transparent", border: isActive ? COLORS.accent2 : COLORS.grid_border, glow: isActive ? `0 0 12px ${COLORS.accent2}66` : "none", text: COLORS.text },
  };

  const style = colorMap[status] || colorMap.default;

  return (
    <div style={{
      width: 62, height: 62,
      border: `2px solid ${style.border}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: value !== "" ? 22 : 14,
      fontFamily: "'Orbitron', monospace",
      fontWeight: "700",
      color: style.text,
      background: style.bg,
      boxShadow: style.glow,
      borderRadius: 4,
      transition: "all 0.3s ease",
      animation: revealed ? `flipIn 0.4s ease ${colIndex * 0.12}s both` : isCurrentRow && value !== "" ? "pulse 0.15s ease" : "none",
      cursor: onClick ? "pointer" : "default",
      position: "relative",
      overflow: "hidden",
    }}
    onClick={onClick}>
      {value !== "" && value}
      {status === "correct" && <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, transparent 40%, #ffffff22 50%, transparent 60%)", pointerEvents: "none" }} />}
    </div>
  );
}

function Grid({ attempts, currentGuess, activeCell, onCellClick, sequence, maxAttempts }) {
  const rows = [];
  for (let r = 0; r < maxAttempts; r++) {
    const isRevealed = r < attempts.length;
    const isCurrentRow = r === attempts.length;
    const attempt = isRevealed ? attempts[r] : null;
    const feedback = isRevealed ? evaluateGuess(attempt.guess, sequence) : null;

    rows.push(
      <div key={r} style={{ display: "flex", gap: 8 }}>
        {Array.from({ length: SEQUENCE_LENGTH }).map((_, c) => {
          let value = "";
          let status = null;
          if (isRevealed && attempt) {
            value = attempt.guess[c] ?? "";
            status = feedback[c];
          } else if (isCurrentRow) {
            value = currentGuess[c] !== undefined ? currentGuess[c] : "";
          }
          return (
            <Cell
              key={c}
              value={value === "" ? "" : String(value)}
              status={status}
              isActive={isCurrentRow && c === activeCell}
              isCurrentRow={isCurrentRow}
              colIndex={c}
              revealed={isRevealed}
              onClick={isCurrentRow && onCellClick ? () => onCellClick(c) : undefined}
            />
          );
        })}
      </div>
    );
  }
  return <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{rows}</div>;
}

// Renders the numeric keypad and localized action buttons.
function NumPad({ onNumber, onDelete, onSubmit, onMoveRight, disabled, texts }) {
  const nums = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];
  const keypadRowWidth = 280;
  const btnBase = {
    fontFamily: "'Orbitron', monospace",
    fontWeight: "700",
    fontSize: 18,
    border: `1px solid ${COLORS.accent1}66`,
    borderRadius: 4,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.15s",
    background: "transparent",
    color: COLORS.text,
    opacity: disabled ? 0.5 : 1,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center", width: keypadRowWidth }}>
        {nums.map((n) => (
          <button key={n} onClick={() => !disabled && onNumber(n)} style={{ ...btnBase, width: 48, height: 48, boxShadow: `0 0 8px ${COLORS.accent1}33` }}
            onMouseEnter={e => { if (!disabled) { e.target.style.background = `${COLORS.accent1}22`; e.target.style.boxShadow = `0 0 16px ${COLORS.accent1}88`; }}}
            onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.boxShadow = `0 0 8px ${COLORS.accent1}33`; }}>
            {n}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, width: keypadRowWidth }}>
        <button onClick={() => !disabled && onDelete()} style={{ ...btnBase, flex: 1, height: 40, border: `1px solid #ff444466`, boxShadow: "0 0 8px #ff444433", fontSize: 13 }}
          onMouseEnter={e => { if (!disabled) { e.target.style.background = "#ff444422"; e.target.style.boxShadow = "0 0 16px #ff444488"; }}}
          onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.boxShadow = "0 0 8px #ff444433"; }}>
          {texts.deleteAction}
        </button>
        <button onClick={() => !disabled && onSubmit()} style={{ ...btnBase, flex: 1, height: 40, border: `1px solid ${COLORS.correct}66`, boxShadow: `0 0 8px ${COLORS.correct}33`, color: COLORS.correct, fontSize: 13 }}
          onMouseEnter={e => { if (!disabled) { e.target.style.background = `${COLORS.correct}22`; e.target.style.boxShadow = `0 0 16px ${COLORS.correct}88`; }}}
          onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.boxShadow = `0 0 8px ${COLORS.correct}33`; }}>
          {texts.submitAction}
        </button>
        <button onClick={() => !disabled && onMoveRight()} style={{ ...btnBase, flex: 1, height: 40, border: `1px solid #ff444466`, boxShadow: "0 0 8px #ff444433", fontSize: 13 }}
          onMouseEnter={e => { if (!disabled) { e.target.style.background = "#ff444422"; e.target.style.boxShadow = "0 0 16px #ff444488"; }}}
          onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.boxShadow = "0 0 8px #ff444433"; }}>
          {texts.moveRightAction}
        </button>
      </div>
    </div>
  );
}

// Shows the rule hint after the first miss and a stuck suggestion after repeated misses.
function HintBanner({ attempts, rule, sequence, texts }) {
  if (attempts.length < 1) return null;

  // Detect if, after at least 2 attempts, the player has never matched
  // any number in the sequence (no yellow or green cells at all).
  const shouldSuggestNumber = attempts.length >= 2 && attempts.every((attempt) => {
    const feedback = evaluateGuess(attempt.guess, sequence);
    return feedback.every((status) => status === "absent");
  });

  // Suggest a guaranteed number from the target sequence when the player
  // is completely stuck after 2 attempts.
  const suggestedNumber = shouldSuggestNumber ? sequence[0] : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{
        padding: "8px 16px",
        border: `1px solid ${COLORS.present}66`,
        borderRadius: 4,
        color: COLORS.present,
        fontFamily: "'Share Tech Mono', monospace",
        fontSize: 12,
        textAlign: "center",
        boxShadow: `0 0 12px ${COLORS.present}33`,
        background: `${COLORS.present}11`,
        animation: "fadeIn 0.5s ease",
      }}>
        {texts.hintTitle}: {texts.hints[rule] || texts.defaultHint}
      </div>

      {shouldSuggestNumber && (
        <div style={{
          padding: "8px 16px",
          border: `1px solid ${COLORS.accent2}66`,
          borderRadius: 4,
          color: COLORS.accent2,
          fontFamily: "'Share Tech Mono', monospace",
          fontSize: 12,
          textAlign: "center",
          boxShadow: `0 0 12px ${COLORS.accent2}33`,
          background: `${COLORS.accent2}11`,
          animation: "fadeIn 0.5s ease",
        }}>
          {texts.stuckSuggestionPrefix} <strong>{suggestedNumber}</strong>
        </div>
      )}
    </div>
  );
}

// Displays the localized end-of-game summary with revealed sequence and rule.
function ResultModal({ won, sequence, rule, score, onReset, lang, texts }) {
  const ruleName = RULES[rule]?.label?.[lang] || rule;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 100,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "#00000088", backdropFilter: "blur(4px)",
      animation: "fadeIn 0.3s ease",
    }}>
      <div style={{
        background: COLORS.panel,
        border: `2px solid ${won ? COLORS.correct : COLORS.accent1}`,
        boxShadow: `0 0 40px ${won ? COLORS.correct : COLORS.accent1}66`,
        borderRadius: 8,
        padding: "32px 40px",
        textAlign: "center",
        maxWidth: 360,
        animation: "scaleIn 0.3s ease",
      }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>{won ? "🏆" : "💀"}</div>
        <div style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: 24,
          fontWeight: "900",
          color: won ? COLORS.correct : COLORS.accent1,
          textShadow: `0 0 20px ${won ? COLORS.correct : COLORS.accent1}`,
          marginBottom: 16,
          letterSpacing: 2,
        }}>
          {won ? texts.resultVictory : texts.resultGameOver}
        </div>
        <div style={{ fontFamily: "'Share Tech Mono', monospace", color: COLORS.text, marginBottom: 8, fontSize: 13 }}>
          {texts.sequenceLabel}: <span style={{ color: COLORS.accent2 }}>{sequence.join(" → ")}</span>
        </div>
        <div style={{ fontFamily: "'Share Tech Mono', monospace", color: "#9999cc", marginBottom: 4, fontSize: 12 }}>
          {texts.ruleLabel}: <span style={{ color: COLORS.present }}>{ruleName}</span>
        </div>
        {won && (
          <div style={{ fontFamily: "'Orbitron', monospace", color: COLORS.accent3, fontSize: 28, marginTop: 12, textShadow: `0 0 15px ${COLORS.accent3}` }}>
            {score} <span style={{ fontSize: 13, color: "#9999cc" }}>{texts.pointsLabel}</span>
          </div>
        )}
        <button onClick={onReset} style={{
          marginTop: 20,
          fontFamily: "'Orbitron', monospace",
          padding: "12px 28px",
          background: "transparent",
          border: `2px solid ${COLORS.accent2}`,
          color: COLORS.accent2,
          cursor: "pointer",
          borderRadius: 4,
          fontSize: 14,
          fontWeight: "700",
          boxShadow: `0 0 16px ${COLORS.accent2}44`,
          letterSpacing: 1,
        }}
          onMouseEnter={e => e.target.style.background = `${COLORS.accent2}22`}
          onMouseLeave={e => e.target.style.background = "transparent"}>
          {texts.playAgain}
        </button>
      </div>
    </div>
  );
}

// Renders the IT/EN toggle using the same visual behavior as Synthword.
function LangToggle({ lang, onChange, texts }) {
  const isEN = lang === "en";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 200, justifyContent: "center" }}>
      {/* <span style={{
        fontFamily: "'Orbitron', monospace",
        fontSize: 10,
        letterSpacing: 2,
        color: "#fafafa",
      }}>
        {texts.langLabel}
      </span> */}
      <div
        onClick={() => onChange(isEN ? "it" : "en")}
        style={{
          position: "relative",
          width: 96,
          height: 30,
          display: "flex",
          border: `1px solid ${COLORS.accent2}55`,
          borderRadius: 4,
          cursor: "pointer",
          overflow: "hidden",
          background: "#140028",
          boxShadow: `0 0 10px ${COLORS.accent2}22`,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 2,
            bottom: 2,
            width: "calc(50% - 2px)",
            left: isEN ? "calc(50% + 2px)" : 2,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${COLORS.accent2}, ${COLORS.accent1})`,
            boxShadow: `0 0 12px ${COLORS.accent2}66`,
            transition: "left 0.25s cubic-bezier(0.4,0,0.2,1)",
          }}
        />
        <div style={{
          flex: 1,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Orbitron', monospace",
          fontSize: 11,
          fontWeight: "700",
          color: isEN ? "#7777aa" : "#0b0b1c",
        }}>IT</div>
        <div style={{
          flex: 1,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Orbitron', monospace",
          fontSize: 11,
          fontWeight: "700",
          color: isEN ? "#0b0b1c" : "#7777aa",
        }}>EN</div>
      </div>
    </div>
  );
}

// Shows the home screen where the player picks language and difficulty.
function DifficultySelector({ onSelect, lang, onLangChange, texts }) {
  const diffs = [
    { key: "easy", color: COLORS.correct, icon: "⚡" },
    { key: "medium", color: COLORS.present, icon: "🔥" },
    { key: "hard", color: COLORS.accent1, icon: "💀" },
  ];
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 50,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{ textAlign: "center", animation: "fadeIn 0.5s ease" }}>
        <LangToggle lang={lang} onChange={onLangChange} texts={texts} />
        <div style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: 13,
          color: COLORS.accent2,
          letterSpacing: 4,
          marginBottom: 32,
          textShadow: `0 0 10px ${COLORS.accent2}`,
        }}>
          {texts.chooseDifficulty}
        </div>
        <div style={{ display: "flex", gap: 16, justifyContent: "center", width: "100%", maxWidth: 560, padding: "0 16px" }}>
          {diffs.map(d => (
            <button key={d.key} onClick={() => onSelect(d.key)} style={{
              fontFamily: "'Orbitron', monospace",
              fontWeight: "900",
              fontSize: 14,
              padding: "16px 24px",
              background: "transparent",
              border: `2px solid ${d.color}`,
              color: d.color,
              cursor: "pointer",
              borderRadius: 6,
              boxShadow: `0 0 20px ${d.color}44`,
              letterSpacing: 2,
              transition: "all 0.2s",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = `${d.color}22`; e.currentTarget.style.boxShadow = `0 0 30px ${d.color}88`; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.boxShadow = `0 0 20px ${d.color}44`; }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{d.icon}</div>
              {texts.difficulty[d.key]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================

export default function Numdle() {
  const [lang, setLang] = useState("it");
  const [difficulty, setDifficulty] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [attempts, setAttempts] = useState([]);
  // currentGuess is now an array of SEQUENCE_LENGTH strings, one per cell
  const [currentGuess, setCurrentGuess] = useState(Array(SEQUENCE_LENGTH).fill(""));
  const [activeCell, setActiveCell] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [error, setError] = useState("");
  const [score, setScore] = useState(0);
  const [startTime] = useState(Date.now());
  const texts = TEXTS[lang];

  // Starts a new game for the selected difficulty using the configured seed mode.
  const startGame = useCallback((diff) => {
    setDifficulty(diff);
    const data = getConfiguredGame(diff);
    setGameData(data);
    setAttempts([]);
    setCurrentGuess(Array(SEQUENCE_LENGTH).fill(""));
    setActiveCell(0);
    setGameOver(false);
    setWon(false);
    setError("");
  }, []);

  // Returns to the home screen so the player can choose language and difficulty again.
  const resetGame = useCallback(() => {
    setDifficulty(null);
    setGameData(null);
  }, []);

  // Changes the active UI language while keeping gameplay logic untouched.
  const handleLangChange = useCallback((nextLang) => {
    setLang(nextLang);
  }, []);

  const handleNumber = useCallback((n) => {
    // Handle numeric input without side effects inside state updaters.
    // This prevents double cursor jumps in React StrictMode.
    if (gameOver) return;

    const next = [...currentGuess];
    const cell = next[activeCell];
    let shouldAdvance = false;

    if (cell === "") {
      // Empty cell: place first digit.
      next[activeCell] = String(n);
      // Zero is considered a complete value, so move immediately.
      if (n === 0) shouldAdvance = true;
    } else if (cell.length === 1) {
      // One digit present: try to compose a 2-digit number.
      const combined = parseInt(cell, 10) * 10 + n;
      if (combined <= 99) {
        next[activeCell] = String(combined);
        shouldAdvance = true;
      }
      // If combined > 99, ignore the input.
    }
    // If cell already has 2 digits, ignore the input.

    setCurrentGuess(next);
    if (shouldAdvance) {
      setActiveCell(c => Math.min(c + 1, SEQUENCE_LENGTH - 1));
    }
    setError("");
  }, [gameOver, activeCell, currentGuess]);

  const handleDelete = useCallback(() => {
    // Handle delete without side effects inside state updaters.
    if (gameOver) return;

    const next = [...currentGuess];
    const cell = next[activeCell];
    let shouldMoveBack = false;

    if (cell.length > 1) {
      // Remove last digit from current cell.
      next[activeCell] = cell.slice(0, -1);
    } else if (cell.length === 1) {
      // Clear current cell.
      next[activeCell] = "";
    } else if (activeCell > 0) {
      // Empty cell: move back one cell and clear it.
      next[activeCell - 1] = "";
      shouldMoveBack = true;
    }

    setCurrentGuess(next);
    if (shouldMoveBack) {
      setActiveCell(c => c - 1);
    }
    setError("");
  }, [gameOver, activeCell, currentGuess]);

  // Moves the active cell one step to the right, clamped to the last cell.
  const handleMoveRight = useCallback(() => {
    if (gameOver) return;
    setActiveCell(c => Math.min(c + 1, SEQUENCE_LENGTH - 1));
    setError("");
  }, [gameOver]);

  // Validates and submits the current row without changing core scoring or evaluation logic.
  const handleSubmit = useCallback(() => {
    if (gameOver || !gameData) return;
    if (currentGuess.some(c => c === "")) {
      setError(texts.validationError);
      return;
    }
    const guessNums = currentGuess.map(c => parseInt(c, 10));
    const newAttempts = [...attempts, { guess: guessNums }];
    setAttempts(newAttempts);
    setCurrentGuess(Array(SEQUENCE_LENGTH).fill(""));
    setActiveCell(0);

    const isWon = guessNums.every((v, i) => v === gameData.sequence[i]);
    if (isWon) {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const s = Math.max(0, 100 - (newAttempts.length - 1) * 10 - Math.floor(elapsed / 10));
      setScore(s);
      setWon(true);
      setGameOver(true);
    } else if (newAttempts.length >= MAX_ATTEMPTS) {
      setGameOver(true);
    }
  }, [gameOver, gameData, currentGuess, attempts, startTime, texts]);

  // Keyboard support
  useEffect(() => {
    const handler = (e) => {
      if (e.key >= "0" && e.key <= "9") handleNumber(parseInt(e.key));
      else if (e.key === "Backspace") handleDelete();
      else if (e.key === "Enter") handleSubmit();
      else if (e.key === "ArrowLeft") setActiveCell(c => Math.max(c - 1, 0));
      else if (e.key === "ArrowRight") setActiveCell(c => Math.min(c + 1, SEQUENCE_LENGTH - 1));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleNumber, handleDelete, handleSubmit]);

  const diffColors = { easy: COLORS.correct, medium: COLORS.present, hard: COLORS.accent1 };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Share+Tech+Mono&display=swap');
        @keyframes twinkle { 0%,100%{opacity:0.3} 50%{opacity:1} }
        @keyframes flipIn { 0%{transform:rotateX(-90deg);opacity:0} 100%{transform:rotateX(0);opacity:1} }
        @keyframes pulse { 0%{transform:scale(1)} 50%{transform:scale(1.12)} 100%{transform:scale(1)} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes scaleIn { from{transform:scale(0.8);opacity:0} to{transform:scale(1);opacity:1} }
        @keyframes scanline { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
        @keyframes glitch { 0%,100%{text-shadow:0 0 20px #ff00ff,0 0 40px #ff00ff} 50%{text-shadow:-2px 0 #00ffff,2px 0 #ff00ff,0 0 40px #ff00ff} }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0a0a1a; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #ff00ff44; }
      `}</style>

      <SynthwaveBackground />

      {/* Scanline overlay */}
      <div style={{ position: "fixed", inset: 0, zIndex: 1, pointerEvents: "none", overflow: "hidden" }}>
        <div style={{
          position: "absolute", width: "100%", height: "2px",
          background: "linear-gradient(transparent, #ffffff08, transparent)",
          animation: "scanline 8s linear infinite",
        }} />
      </div>

      <div style={{ position: "relative", zIndex: 2, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", padding: "20px 16px 40px" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <h1 style={{
            fontFamily: "'Orbitron', monospace",
            fontWeight: "900",
            fontSize: "clamp(36px, 8vw, 64px)",
            background: `linear-gradient(90deg, ${COLORS.accent1}, ${COLORS.accent2})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: 6,
            animation: "glitch 3s ease-in-out infinite",
            marginBottom: 4,
          }}>
            NUMDLE
          </h1>
          <p style={{
            fontFamily: "'Share Tech Mono', monospace",
            color: "#6666aa",
            fontSize: 11,
            letterSpacing: 3,
          }}>
            {texts.subtitle}
          </p>
        </div>

        {!difficulty && (
          <DifficultySelector
            onSelect={startGame}
            lang={lang}
            onLangChange={handleLangChange}
            texts={texts}
          />
        )}

        {difficulty && gameData && (
          <>
            {/* Info bar */}
            <div style={{ display: "flex", gap: 16, marginBottom: 20, alignItems: "center" }}>
              <div style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: 10,
                fontWeight: "700",
                color: diffColors[difficulty],
                border: `1px solid ${diffColors[difficulty]}66`,
                padding: "4px 12px",
                borderRadius: 20,
                letterSpacing: 2,
                boxShadow: `0 0 8px ${diffColors[difficulty]}44`,
              }}>
                {texts.difficulty[difficulty]}
              </div>
              <div style={{
                fontFamily: "'Share Tech Mono', monospace",
                fontSize: 12,
                color: "#6666aa",
              }}>
                {texts.attempts(attempts.length, MAX_ATTEMPTS)}
              </div>
            </div>

            {/* Grid */}
            <div style={{ marginBottom: 20 }}>
              <Grid
                attempts={attempts}
                currentGuess={currentGuess}
                activeCell={activeCell}
                sequence={gameData.sequence}
                maxAttempts={MAX_ATTEMPTS}
                onCellClick={!gameOver ? setActiveCell : undefined}
              />
            </div>

            {/* Hint */}
            <div style={{ marginBottom: 16, width: "100%", maxWidth: 340 }}>
              <HintBanner attempts={attempts} rule={gameData.rule} sequence={gameData.sequence} texts={texts} />
            </div>

            {/* Error */}
            {error && (
              <div style={{
                fontFamily: "'Share Tech Mono', monospace",
                color: COLORS.accent1,
                fontSize: 12,
                marginBottom: 10,
                textShadow: `0 0 8px ${COLORS.accent1}`,
                animation: "pulse 0.3s ease",
              }}>
                ⚠ {error}
              </div>
            )}

            {/* Numpad */}
            <NumPad
              onNumber={handleNumber}
              onDelete={handleDelete}
              onSubmit={handleSubmit}
              onMoveRight={handleMoveRight}
              disabled={gameOver}
              texts={texts}
            />

            {/* Legend */}
            <div style={{ display: "flex", gap: 16, marginTop: 24, fontFamily: "'Share Tech Mono', monospace", fontSize: 11, color: "#6666aa" }}>
              {[
                { color: COLORS.correct, label: texts.legend.correct },
                { color: COLORS.present, label: texts.legend.present },
                { color: "#2a2a6a", label: texts.legend.absent },
              ].map(({ color, label }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 14, height: 14, background: color, borderRadius: 2, boxShadow: `0 0 6px ${color}88` }} />
                  {label}
                </div>
              ))}
            </div>

            {/* Change difficulty */}
            {!gameOver && (
              <button onClick={resetGame} style={{
                marginTop: 20,
                fontFamily: "'Share Tech Mono', monospace",
                fontSize: 11,
                color: "#6666aa",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                letterSpacing: 1,
                textDecoration: "underline",
              }}>
                {texts.changeDifficulty}
              </button>
            )}
          </>
        )}
      </div>

      {gameOver && gameData && (
        <ResultModal
          won={won}
          sequence={gameData.sequence}
          rule={gameData.rule}
          score={score}
          onReset={resetGame}
          lang={lang}
          texts={texts}
        />
      )}
    </>
  );
}
